import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const appleFont = {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif"
  };

  useEffect(() => {
    AOS.init({ duration: 700, once: true, easing: 'ease-out' });
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/api/faqs');
      if (!response.ok) throw new Error('Failed to fetch FAQs');
      const data = await response.json();
      if (data.success && data.faqs && Array.isArray(data.faqs) && data.faqs.length > 0) {
        setFaqs(data.faqs);
      } else {
        setFaqs([]);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError('FAQ তথ্য লোড করতে সমস্যা হয়েছে। পরে আবার চেষ্টা করুন।');
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Loading
  if (loading) {
    return (
      <div style={appleFont} className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-400 tracking-wide">Loading FAQs...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={appleFont} className="bg-white min-h-screen py-16 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto border border-gray-200 rounded-2xl shadow-sm overflow-hidden">

        {/* Header */}
        <div className="px-10 pt-12 pb-10 border-b border-gray-100" data-aos="fade-down">
          <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">Support</p>
          <h1 className="text-4xl font-semibold text-gray-900 tracking-tight mb-3">
            Frequently Asked Questions
          </h1>
          <p className="text-sm text-gray-500 leading-relaxed">
            Welcome to our FAQ section! Here you'll find answers to the most common questions about our products, ordering, shipping, and returns.
          </p>
        </div>

        {/* Error State */}
        {error && !loading && (
          <div className="px-10 py-8" data-aos="fade-up">
            <div className="border border-gray-200 rounded-xl p-5 flex items-start gap-3">
              <svg className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-gray-700 mb-2">{error}</p>
                <button
                  onClick={fetchFAQs}
                  className="text-xs font-medium text-gray-900 underline underline-offset-2 hover:text-gray-600 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FAQ List */}
        {!loading && !error && faqs.length > 0 && (
          <div className="px-10 py-8 space-y-0 divide-y divide-gray-100">
            {faqs.map((faq, index) => (
              <div
                key={faq.id || index}
                data-aos="fade-up"
                data-aos-delay={index * 40}
              >
                <button
                  className="w-full flex justify-between items-start py-5 text-left gap-4 group"
                  onClick={() => toggleFAQ(index)}
                  aria-expanded={index === openIndex}
                >
                  <div className="flex items-start gap-3 flex-1">
                    <span className="flex-shrink-0 text-xs font-semibold text-gray-300 mt-1 w-5 text-right">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className={`text-sm font-medium leading-relaxed transition-colors duration-200 ${index === openIndex ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'}`}>
                      {faq.question}
                    </h3>
                  </div>
                  <div className="flex-shrink-0 mt-0.5">
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${index === openIndex ? 'rotate-180 text-gray-900' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Answer */}
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${index === openIndex ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="pl-8 pb-5 pr-6">
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && faqs.length === 0 && (
          <div className="px-10 py-16 text-center" data-aos="fade-up">
            <svg className="w-12 h-12 text-gray-200 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium text-gray-900 mb-1">No FAQs Available</p>
            <p className="text-sm text-gray-400 mb-5">এই মুহূর্তে কোনো FAQ পাওয়া যায়নি।</p>
            <button
              onClick={fetchFAQs}
              className="px-5 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors"
            >
              Refresh
            </button>
          </div>
        )}

        {/* Footer Note */}
        {!loading && faqs.length > 0 && (
          <div className="px-10 py-8 border-t border-gray-100" data-aos="fade-up">
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">Still have questions?</p>
            <p className="text-sm text-gray-500 leading-relaxed">
              Our customer service team is here to help. Contact us through our Contact page or reach out via email.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default FAQPage;